import * as React from 'react';
import { useState, useRef } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";

const FullscreenWrapper = ({ children }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const containerRef = useRef(null);

  const handleFullscreenChange = () => {
    setIsFullscreen(!!document.fullscreenElement);
  };

  const enterFullscreen = async () => {
    if (containerRef.current) {
      try {
        // requestFullScreen is a method that is available on DOM elements
        // when its called, it puts the element and all its children into fullscreen mode
        if (containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen();
        } else if (containerRef.current.webkitRequestFullscreen) {
          await containerRef.current.webkitRequestFullscreen();
        } else if (containerRef.current.msRequestFullscreen) {
          await containerRef.current.msRequestFullscreen();
        } else {
          // If we're in a browser that doesn't support/provide fullscreen view, we can use a modal instead
          //   to polyfill the fullscreen view
          setShowModal(true);
        }
      } catch (error) {
        // also, we open the content inside a modal, if the fullscreen request fails for some reason
        setShowModal(true);
        console.log(
          "Error oepning fullscreen view. Opening modal instead: ",
          error
        );
      }
    }
  };

  const exitFullscreen = async () => {
    // we exit the fullscreen, otherwise close the modal if polyfilled
    // writing this first because the later async function calls never let the control return back to the function
    // might just be because of "await" but, the promise does get resolved, so I wonder why
    setShowModal(false);

    // note that we're calling the exitFullScreen method on the document, NOT on the element
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      await document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      await document.msExitFullscreen();
    }
  };

  React.useEffect(() => {
    // we can use an Abort Controller here btw, if we don't want this kind of syntax
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  const FullscreenButton = () => (
    <Button
      variant="outline"
      size="icon"
      className="absolute top-4 right-4 z-50"
      onClick={isFullscreen || showModal ? exitFullscreen : enterFullscreen}
    >
      {isFullscreen || showModal ? (
        <Minimize2 className="h-4 w-4" />
      ) : (
        <Maximize2 className="h-4 w-4" />
      )}
    </Button>
  );

  return (
    <>
      <div className="relative bg-white m-auto" ref={containerRef}>
        <FullscreenButton />
        <div
          className={
            isFullscreen ? "w-screen h-screen overflow-x-auto" : "w-full h-full"
          }
        >
          {children}
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-screen max-h-screen w-screen h-full p-6 overflow-x-auto">
          <FullscreenButton />
          {children}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FullscreenWrapper;
